import React from 'react';
import Popover from '@mui/material/Popover';
import { Button, Box, Typography, IconButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'common/hook';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import { useTranslation } from 'react-i18next';
import { createStyleSheet } from 'utils/helpers';
import SyncIcon from '@mui/icons-material/Sync';
import AccountConstants, {
    convertValueAccountStatus,
    convertValueAccountType,
} from '../constants';
import { accountFilterSchema } from 'common/schema';
import {
    clearAccountSearchForm,
    setAccountSearchForm,
} from 'slices/accountSlice';

interface IAccountFilterProps {
    open: boolean;
    headerFilterId: string | undefined;
    anchorEl: HTMLButtonElement | null;
    handleClose: () => void;
}

const AccountFilter: React.FC<IAccountFilterProps> = ({
    open,
    headerFilterId,
    anchorEl,
    handleClose,
}) => {
    const dispatch = useAppDispatch();
    const accountsSearchForm = useAppSelector(
        (state) => state.account.accountsSearchForm
    );
    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(accountFilterSchema),
        defaultValues: {
            status: accountsSearchForm.status || [AccountConstants.status.ALL],
            type: accountsSearchForm.type || [AccountConstants.type.ALL],
            startDate: accountsSearchForm.startDate || '',
            endDate: accountsSearchForm.endDate || '',
        },
    });

    const { handleSubmit, reset, watch, setValue } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const params = { ...data };
        dispatch(setAccountSearchForm(params));
        handleClose();

        return;
    });

    const handleRenewForm = () => {
        dispatch(clearAccountSearchForm());
        reset({
            status: [AccountConstants.status.ALL],
            type: [AccountConstants.type.ALL],
            startDate: '',
            endDate: '',
        });
    };

    const topPixel = 104;

    const datePickerStyle = {
        '& .MuiInputBase-root': {
            height: '29px',
            borderRadius: '10px',
        },
        '& .MuiIconButton-root svg': {
            width: '14px',
            height: '14px',
        },
    };

    const checkBoxStyle = {
        '& .MuiFormControlLabel-label': {
            fontSize: '20px',
        },
    };

    const groupTitleStyle = {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '29px',
        marginBottom: '15px',
    };

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    React.useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setValue('endDate', '');
            }
        }
    }, [startDate, endDate, setValue]);

    return (
        <Popover
            id={headerFilterId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            sx={{
                '& .MuiPaper-root.MuiPopover-paper': {
                    top: `${topPixel}px !important`,
                    maxHeight: `min(906px, calc(100% - ${topPixel}px))`,
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        padding: '20px 30px 85px',
                        width: '430px',
                        overflowY: 'auto',
                        maxHeight: '700px',
                    }}
                >
                    <Box
                        mb={'20px'}
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Typography
                            sx={{
                                color: (theme) => theme.palette.primary.main,
                                fontWeight: 800,
                                fontSize: '27px',
                                lineHeight: '33px',
                            }}
                        >
                            {t('layout.header.filter.title')}
                        </Typography>
                        <IconButton
                            onClick={handleRenewForm}
                            aria-label="refresh"
                        >
                            <SyncIcon
                                sx={{
                                    fontSize: 32,
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thời gian
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            gap={1}
                        >
                            {t('layout.header.filter.time.start')}
                            <BaseDatePicker
                                name="startDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                            {t('layout.header.filter.time.end')}
                            <BaseDatePicker
                                name="endDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Trạng thái
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="status"
                                options={
                                    Object.keys(AccountConstants.status).map(
                                        (key) => {
                                            const status =
                                                AccountConstants.status[
                                                    key as keyof typeof AccountConstants.status
                                                ];
                                            return {
                                                label: convertValueAccountStatus(
                                                    status
                                                ),
                                                value: AccountConstants.status[
                                                    key as keyof typeof AccountConstants.status
                                                ],
                                            };
                                        }
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>

                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Loại
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="type"
                                options={
                                    Object.keys(AccountConstants.type).map(
                                        (key) => {
                                            const type =
                                                AccountConstants.type[
                                                    key as keyof typeof AccountConstants.type
                                                ];
                                            return {
                                                label: convertValueAccountType(
                                                    type
                                                ),
                                                value: AccountConstants.type[
                                                    key as keyof typeof AccountConstants.type
                                                ],
                                            };
                                        }
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    display={'flex'}
                    justifyContent={'end'}
                    gap={2}
                    padding={'20px 30px'}
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        style={styles.modalButton}
                    >
                        Đóng
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        aria-label="filter"
                        onClick={onSubmit}
                        style={styles.modalButton}
                    >
                        Lọc
                    </Button>
                </Box>
            </FormProvider>
        </Popover>
    );
};

const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '40px',
        minHeight: '42px',
        minWidth: '84px',
        fontSize: '14px',
        fontWeight: '600',
        borderWidth: '2px',
    },
});

export default AccountFilter;
